// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Card, Group, Modal, Stack, Text, Title } from '@mantine/core';
import type { AppointmentTypeDefinition, AvailableTimeSlot } from '@polarissleep/scheduling';
import type { JSX } from 'react';
import type { ProviderAppointmentCandidate } from '../../scheduling-next/types';
import { formatAppointmentDate } from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';

interface BookingConfirmationProps {
  opened: boolean;
  appointmentType: AppointmentTypeDefinition;
  slot: AvailableTimeSlot | ProviderAppointmentCandidate;
  onConfirm: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  isDuplicateType?: boolean;
}

/**
 * Centered modal confirmation for provider-initiated booking.
 *
 * Copy is third-person (clinician viewpoint) — "the patient" instead of "you"
 * to match the provider-app context.
 * @param root0 - Component props
 * @param root0.opened - Whether the modal is visible
 * @param root0.appointmentType - The selected appointment type
 * @param root0.slot - The selected time slot
 * @param root0.onConfirm - Callback when the clinician confirms the booking
 * @param root0.onBack - Callback to dismiss the modal
 * @param root0.isSubmitting - Whether the booking request is in progress
 * @returns The booking confirmation modal
 */
export function BookingConfirmation({
  opened,
  appointmentType,
  slot,
  onConfirm,
  onBack,
  isSubmitting,
  isDuplicateType = false,
}: BookingConfirmationProps): JSX.Element {
  const start = 'appointment' in slot ? (slot.appointment.start ?? '') : slot.start;
  const end = 'appointment' in slot ? (slot.appointment.end ?? '') : slot.end;
  const dateStr = formatAppointmentDate(start);

  return (
    <Modal opened={opened} onClose={onBack} size="lg" centered withCloseButton={false} padding="xl" radius="lg">
      <Stack gap="lg" align="center">
        <Title order={2}>Confirm Appointment</Title>
        <Text size="sm" c="dimmed" ta="center">
          Please make sure the following information is correct:
        </Text>

        {isDuplicateType && (
          <Alert color="yellow" title="Patient already has this appointment type" w="100%">
            This patient already has an upcoming {appointmentType.display} appointment. Booking this one will create a
            second appointment of the same type.
          </Alert>
        )}

        <Card bg="gray.0" p="lg" radius="md" w="100%">
          <Stack gap="md" align="center">
            <Text fw={600} size="md">
              {appointmentType.display}
            </Text>
            <Text fw={600} size="lg">
              {dateStr}
            </Text>
            <AppointmentTimeWithTimezone
              start={start}
              end={end}
              groupProps={{ justify: 'center' }}
              textProps={{ fw: 600, size: 'lg' }}
            />

            {appointmentType.patientEligibility && (
              <Card withBorder p="md" radius="md" w="100%">
                <Stack gap="xs">
                  <Text fw={600} size="sm" ta="center">
                    What to Expect
                  </Text>
                  <Text size="sm" ta="center">
                    {appointmentType.patientEligibility}
                  </Text>
                </Stack>
              </Card>
            )}

            <Text size="sm" c="dimmed" ta="center">
              When you confirm this appointment, the patient will receive an email with the date, time, and link to join
              the video call. They will also receive two reminders, one day and fifteen minutes before the appointment.
            </Text>
          </Stack>
        </Card>

        <Group justify="center" w="100%">
          <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
            Back
          </Button>
          <Button onClick={onConfirm} loading={isSubmitting} disabled={isSubmitting}>
            Confirm Appointment
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
